/**
 * SMS 발송 폼 컴포넌트 (Phase 4 Batch 5+6 — 발송테스트 가능단계까지).
 *
 * <p>사용처: view/sms/send.html — 단일 페이지 컴포넌트.
 *
 * <p>data-* 속성:
 *   - data-remain: 잔여 SMS 건수 (SSR 초기값)
 *   - data-sender-name: 로그인 사용자명 (디버그용)
 */
export default () => ({
    // 초기값 — init() 에서 data-* 로부터 hydration
    remain: 0,
    hasAuth: false,
    loading: false,

    // 입력 상태
    senderNumber: '',
    rawInput: '',
    phones: [],         // [{ name, phone, display, valid }]
    message: '',
    subject: '',
    forceLms: false,
    inputHint: '',      // 수신번호 직접입력 아래 짧은 안내(중복 제외 등) — 약 1.8초 후 자동 소거(2026-09-16, 09-22 연장)
    _hintTimer: null,

    init() {
        const root = this.$root;
        this.remain = parseInt(root.dataset.remain || '0', 10);
        // 발신번호 select 가 비어있는 경우 hasAuth=false (제출 차단).
        // 2026-09-16: '발신번호를 선택하세요' 플레이스홀더 제거 — 옵션이 1개 이상이면 승인 번호 있음, 첫 번호를 기본 선택(사용자 확정)
        const senderSelect = root.querySelector('select[x-model="senderNumber"]') || root.querySelector('select');
        const options = senderSelect ? senderSelect.querySelectorAll('option') : [];
        this.hasAuth = options.length > 0;
        if (this.hasAuth && !this.senderNumber) {
            this.senderNumber = options[0].value;
        }
    },

    get bytes() {
        // 한글 2바이트, 그 외 1바이트 — MS949 근사 (절대 정확치는 서버 검증)
        let n = 0;
        for (const ch of this.message) {
            n += (ch.charCodeAt(0) > 127) ? 2 : 1;
        }
        return n;
    },

    get isLms() {
        return this.forceLms || this.bytes > 90;
    },

    /**
     * textarea blur / Enter 시 호출 — 입력 텍스트를 phones[] 로 파싱.
     *
     * 입력 양식:
     *   - "이름|01012345678" 또는 "이름|010-1234-5678"
     *   - 번호만: "01012345678" 또는 "010-1234-5678"
     *   - 여러 명은 줄바꿈으로 구분
     *
     * 각 줄에서 콤마/마침표/공백은 제거 (사용자가 잘못 끼워넣은 구분자 흡수 — 레거시 관행).
     */
    parseInput() {
        const raw = (this.rawInput || '').trim();
        if (!raw) return;
        const lines = raw.split(/\r?\n/).map(l => l.trim()).filter(l => l);
        const added = [];
        let dup = 0; // 이미 추가된 번호와 중복 — 조용히 건너뛰면 아무 반응이 없어 보이므로 건수 안내(2026-09-16)
        for (const line of lines) {
            const cleaned = line.replace(/[,. ]/g, '');
            if (!cleaned) continue;
            const parts = cleaned.split('|');
            let name = '';
            let rawPhone;
            if (parts.length >= 2) {
                name = parts[0];
                rawPhone = parts[1];
            } else {
                rawPhone = parts[0];
            }
            const stripped = (rawPhone || '').replace(/-/g, '');
            const valid = /^01[016789]\d{7,8}$/.test(stripped);
            if (this.phones.find(p => p.phone === stripped) || added.find(p => p.phone === stripped)) {
                dup++;
                continue;
            }
            const formatted = this.formatPhone(stripped);
            added.push({
                name,
                phone: stripped,
                display: name ? `${name} ${formatted}` : formatted,
                valid
            });
        }
        this.phones = [...this.phones, ...added];
        this.rawInput = '';
        if (dup > 0) {
            this.showInputHint('중복된 번호 ' + dup + '건은 제외했습니다.');
        }
    },

    /** 입력창 아래 1.8초 안내(1.5→1.8초, 2026-09-22 사용자 — 너무 짧다는 지적) — 연속 호출 시 타이머 갱신. */
    showInputHint(text) {
        this.inputHint = text;
        if (this._hintTimer) clearTimeout(this._hintTimer);
        this._hintTimer = setTimeout(() => { this.inputHint = ''; this._hintTimer = null; }, 1800);
    },

    removePhone(idx) {
        this.phones = this.phones.filter((_, i) => i !== idx);
    },

    formatPhone(stripped) {
        if (!/^01[016789]\d{7,8}$/.test(stripped)) return stripped;
        const splitAt = stripped.length - 4;
        return stripped.substring(0, 3) + '-' + stripped.substring(3, splitAt) + '-' + stripped.substring(splitAt);
    },

    async submit() {
        if (this.loading) return;
        // blur 누락 대비 마지막 한 번 파싱
        this.parseInput();

        // 클라이언트 검증
        if (!this.senderNumber) {
            this.toastWarn('발신번호를 선택해 주세요.');
            return;
        }
        if (this.phones.length === 0) {
            this.toastWarn('수신번호를 1개 이상 입력해 주세요.');
            this.$refs.rcvInput?.focus();
            return;
        }
        const invalids = this.phones.filter(p => !p.valid);
        if (invalids.length > 0) {
            this.toastWarn('올바르지 않은 수신번호가 있습니다. (빨간색)');
            return;
        }
        if (!this.message.trim()) {
            this.toastWarn('메시지를 입력해 주세요.');
            this.$refs.msgInput?.focus();
            return;
        }
        if (this.bytes > 2000) {
            this.toastWarn('메시지가 너무 깁니다. (최대 2000바이트)');
            return;
        }

        const payload = {
            senderNumber: this.senderNumber,
            recipients: this.phones.map(p => ({ name: p.name || '', phone: p.phone })),
            message: this.message,
            subject: this.isLms ? (this.subject || '') : '',
            forceLms: this.forceLms
        };

        this.loading = true;
        try {
            const res = await fetch('/sms/api/send', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload)
            });
            const data = await res.json();
            if (data.success) {
                this.$dispatch('show-toast', {
                    type: 'success',
                    title: '발송 완료',
                    message: data.message
                });
                this.remain = data.remainAfter;
                // 입력 초기화 (발신번호는 유지)
                this.phones = [];
                this.message = '';
                this.subject = '';
                this.forceLms = false;
            } else {
                this.$dispatch('show-toast', {
                    type: 'error',
                    title: '발송 실패',
                    message: data.message || 'SMS 발송에 실패했습니다.'
                });
            }
        } catch (e) {
            this.$dispatch('show-toast', {
                type: 'error',
                title: '오류',
                message: '요청 중 오류가 발생했습니다.'
            });
        } finally {
            this.loading = false;
        }
    },

    toastWarn(msg) {
        this.$dispatch('show-toast', { type: 'warning', title: '입력 확인', message: msg });
    }
});
